import {
  Alert,
  Banner,
  Button,
  Heading,
  TextLink,
  Tooltip,
} from '@mm/components';
import { InfoIcon } from '@mm/icons';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { LinkTypes } from '@mm/state/model/PromoterModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { showInstaChampAlertSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import instagram from 'assets/instagram.svg';
import instagramIphoneCommentGuardIcon from 'assets/instagramIphoneCommentGuardIcon.png';
import React, { useEffect, useMemo, MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../../common/hooks/use-edit-page-prime-state';
import { InactiveBotAlert } from '../../../components/InactiveBotAlert/InactiveBotAlert';
import { colors } from '../../beta/styles/colors';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialogueSelectorWithReload } from '../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { EditStartBotHeader } from '../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { StartBotsDialogueEditor } from '../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import styles from './InstagramCommentGuardEditor.module.css';
import InstagramPostSelectionForm from './InstagramPostSelectionForm/InstagramPostSelectionForm';
import FrequencyCapControl from '../commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { getDeviceInfo } from '../../../../common/hooks/use-device-info';
import { DialoguePreview } from '../../../../modules/DailoguePreview/DialoguePreview';
import { PublicReplies } from '@mm/components/src/PublicReplies/PublicReplies';
import {
  BubbleTextInput,
  InputFieldRefType,
} from '../../../../common/widgets/common/BubbleTextInput/BubbleTextInput';
import { useActivateIgCommentGuard } from './use-activate-ig-comment-guard';

type Props = {
  kind: LinkTypes;
};

const SUPPORTED_WIDGET_TYPES: WidgetTypes[] = [
  WidgetTypes.QUICK_QUESTION,
  WidgetTypes.QQ_OPTIONS,
  WidgetTypes.QQ_EMAIL,
  WidgetTypes.QQ_PHONE,
  WidgetTypes.QQ_INFO,
  WidgetTypes.ATTRIBUTE,
  WidgetTypes.TAGS,
  WidgetTypes.DELAY,
  WidgetTypes.EMAIL,
  WidgetTypes.IMAGE,
  WidgetTypes.GIF,
  WidgetTypes.FORM,
  WidgetTypes.GALLERY,
  WidgetTypes.TEXT,
  WidgetTypes.SEND_DATA,
  WidgetTypes.NAVIGATE,
  WidgetTypes.SMS_OPT_IN_V2,
  WidgetTypes.TYPING,
];

export const InstagramCommentGuardEditor = ({ kind }: Props) => {
  const chatbot = useSelector(activeChatbotSelector);
  const { widgets, page, promoter } = useEditPagePrimeState(kind);
  const pathGraph = useNewGraphInstance(promoter?.pageId);

  const fbPage = useSelector(activeFacebookPageSelector);
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);
  const promoterCRUDActions = usePromoterCRUDActions();
  const { isMobile } = getDeviceInfo();

  const [showInstaChampAlert, instagramUsername] = useSelector(
    showInstaChampAlertSelector,
  );
  const { customActivateAction, setToggler } =
    useActivateIgCommentGuard(promoter);

  const { getIGListMedia, getIGMedia, clearIGMedia } =
    useInstagramCRUDActions();

  useEffect(() => {
    if (fbPage) {
      getIGListMedia(fbPage.id);
    }
  }, [fbPage?.id]);

  useEffect(() => {
    if (fbPage?.id && promoter?.postId) {
      if (['id', 'ALL'].includes(promoter?.postId)) {
        clearIGMedia();
      } else {
        getIGMedia(fbPage?.id, promoter?.postId);
      }
    }
  }, [promoter?.postId]);

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot?.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.instagramCommentGuardEdit.getLink({
            chatbotId: chatbot?.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
      />
    ) : null;
  }, [promoter, chatbot?.id]);

  if (!promoter || !page) return null;

  return (
    <div className={styles.component}>
      <InactiveChannelAlertSmart instagram type="Comment Guard" />

      <EditStartBotHeader
        promoter={promoter}
        page={page}
        botIsTemplate={!!chatbot.template}
        customActivateAction={customActivateAction}
      />
      <InactiveBotAlert />
      {showInstaChampAlert && (
        <Alert type="warning" showCloseIcon={false}>
          <div>
            This Instagram account was connected via InstaChamp. Making changes
            here may block you from making further changes in InstaChamp.
          </div>
          <Button
            onClick={() => {
              window.location.href = `/instachamp/@${instagramUsername}/post_comments/${promoter.id}`;
            }}
          >
            Edit with InstaChamp
          </Button>
        </Alert>
      )}
      {!isMobile && (
        <Banner
          reverse
          transparentStyle
          title="Set up a Comment Guard to automatically message anyone who leaves a comment on your Instagram post"
          headerIcon={<img src={instagram} className={styles.ig_icon} />}
          arrowColor={colors.accentBlue}
          withArrow
          withBannerHeader
          headerText="What's a Comment Guard?"
        >
          <img
            className={styles.phone_icon}
            src={instagramIphoneCommentGuardIcon}
          />
        </Banner>
      )}
      <div className={styles.editor_container}>
        <div className={styles.content}>
          <div className={styles.editor_block}>
            <Heading className={styles.container_title} size={3}>
              This Comment Guard will work with
            </Heading>
            <InstagramPostSelectionForm
              guard={promoter}
              facebookPage={fbPage}
              setToggler={setToggler}
              customRespondAllName="To all comments"
              customRespondToCommentsName="Only to comments that contain the following keywords"
            />
          </div>
          <Heading
            size={3}
            className={styles.container_title}
            text="Public Reply"
          />
          <PublicReplies
            customSendReply={
              <div className={styles.public_reply_title}>
                Send Public Reply
                <Tooltip
                  anchorElement={
                    <InfoIcon
                      className={styles.public_reply_title_info_icon}
                      color="#262626"
                    />
                  }
                  position={isMobile ? 'bottom' : 'right'}
                  css={{ content: styles.public_reply_tooltip }}
                  align="center"
                  withoutLine
                  useHover
                  title=" Reply with a public comment on top-level user's comment.
                      Create multiple replies and we'll send them at random.
                      Hidden comments are not supported."
                />
              </div>
            }
            promoter={promoter}
            chatbotId={chatbot.id}
            customTextareaComponent={(
              text: string,
              onUpdate: (text: string) => void,
              onClick: () => void,
              maxLength: number,
              editMode: boolean,
              setEditMode: (edit: boolean) => void,
              inputFieldRef: InputFieldRefType,
              ref: MutableRefObject<HTMLDivElement | null>,
            ) => (
              <BubbleTextInput
                ref={ref}
                value={text}
                editMode={editMode}
                setEditMode={setEditMode}
                onClick={onClick}
                updateText={onUpdate}
                maxLength={maxLength}
                newCharacterCountDesign
                showAttributePicker={false}
                inputFieldRefToSet={inputFieldRef}
              />
            )}
          />

          <div className={styles.frequency_cap_container}>
            <FrequencyCapControl
              guard={promoter}
              chatbotId={chatbot.id}
              css={{
                label: styles.frequency_cap_label,
              }}
            />
          </div>
          <Heading className={styles.container_title} size={3}>
            Choose or create a Dialogue to send to users who comment on your
            Instagram post
          </Heading>
          <div className={styles.dialogue_editor_container}>
            <PathGraphContext.Provider value={pathGraph}>
              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                forInstagram
                chatbotId={chatbot.id}
                widgets={widgets}
                widgetsToExclude={widgetsToExclude}
                dialogueId={promoter.pageId}
                platforms={['instagram']}
                showAttributesStartWarning
                firstWidgetNotAllowedWarning={{
                  showWarning: true,
                  message: `This widget type is NOT supported as the first message in your Instagram Comment Guard.
                   Please use a Quick Question widget instead.`,
                  startbotType: 'instagram_cg',
                }}
                disableFirstWidgetConditionalLogic
              />
            </PathGraphContext.Provider>
          </div>
        </div>
        {promoter.pageId && (
          <div className={styles.preview_container}>
            <DialoguePreview
              platforms={['instagram']}
              dialogueId={promoter?.pageId}
              style={{ top: -20, marginTop: 20 }}
              className={styles.preview}
              sticky
            />
          </div>
        )}
      </div>
    </div>
  );
};
